import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquare, ExternalLink, Loader2 } from "lucide-react";
import api from "../../lib/api";
import ChatThreadView from "./ChatThreadView";

/**
 * EntityChatPanel — Wave 19 #85.
 *
 * Embedded chat tab on JC / Ticket / Task detail pages. Resolves (or
 * auto-creates) the thread bound to the entity, then renders the
 * standard ChatThreadView.
 *
 * Props:
 *   kind: "jobcard" | "ticket" | "task"
 *   entityId: string
 */
export default function EntityChatPanel({ kind, entityId }) {
  const [threadId, setThreadId] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!kind || !entityId) return;
      try {
        const { data } = await api.get(`/chats/by-entity/${kind}/${entityId}`);
        if (!cancelled) setThreadId(data?.id);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Could not load chat.");
      }
    })();
    return () => { cancelled = true; };
  }, [kind, entityId]);

  if (error) {
    return (
      <div className="hv-card p-4 text-xs text-rose-200" data-testid="entity-chat-panel-error">
        {error}
      </div>
    );
  }
  if (!threadId) {
    return (
      <div className="hv-card p-4 text-xs text-[#a0aabf] flex items-center gap-2" data-testid="entity-chat-panel-loading">
        <Loader2 className="w-3 h-3 animate-spin" /> Opening chat…
      </div>
    );
  }
  return (
    <div data-testid="entity-chat-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] flex items-center gap-1.5">
          <MessageSquare className="w-3 h-3 text-rose-300" /> Chat
        </div>
        <Link
          to={`/chat/${threadId}`}
          className="hv-btn hv-btn-ghost text-[10px]"
          data-testid="entity-chat-open-fullscreen"
        >
          <ExternalLink className="w-3 h-3" /> Full screen
        </Link>
      </div>
      <ChatThreadView threadId={threadId} embedded />
    </div>
  );
}
